import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { useUser } from "../contexts/useUser";
import { API, Storage } from "aws-amplify";
import { useQueryClient } from "@tanstack/react-query";

const FileUploader = ({
  agentId,
  projectId,
}: {
  agentId: string;
  projectId: string;
}) => {
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const { user } = useUser();
  const [ownershipAttestation, setOwnershipAttestation] = useState("");
  const [ownershipAttestationError, setOwnershipAttestationError] =
    useState("");

  const [ragSplitter, setRagSplitter] = useState("default");
  const [ragSplitterError, setRagSplitterError] = useState("");
  const [ragChunkSize, setRagChuckSize] = useState("1000");
  const [ragChunkSizeError, setRagChunkSizeError] = useState("");
  const [ragChunkOverlap, setRagChuckOverlap] = useState("10");
  const [ragChunkOverlapError, setRagChunkOverlapError] = useState("");
  const [fileTooBigError, setFileTooBigError] = useState(""); 

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();

  const handleFileUpload = (e: any) => {
    const file = e.target.files?.[0];

    if (file.size > 5000000) {
      setFileTooBigError("File too large. Max size is 5MB. Email support for an exception (help@llmasaservice.io)");
      return;
    }
    else {
      setFileTooBigError("");
    }

    if (file) {
      setSelectedFile(file);
    }
  };

  const onSubmit = async () => {
    if (!selectedFile) {
      console.error("No file selected");
      return;
    }

    if (!ownershipAttestation || ownershipAttestation.length < 3) {
      setOwnershipAttestationError("Please enter your full name");
      return;
    } else {
      setOwnershipAttestationError("");
    }

    if (
      ragChunkSize.length === 0 ||
      isNaN(parseInt(ragChunkSize)) ||
      parseInt(ragChunkSize) < 1 ||
      parseInt(ragChunkSize) > 10000
    ) {
      setRagChunkSizeError("Please enter a number 1 to 10000 characters");
      return;
    } else {
      setRagChunkSizeError("");
    }

    if (
      ragChunkOverlap.length === 0 ||
      isNaN(parseInt(ragChunkOverlap)) ||
      parseInt(ragChunkOverlap) < 0 ||
      parseInt(ragChunkOverlap) >= parseInt(ragChunkSize)
    ) {
      setRagChunkOverlapError(
        "Please enter a number 0 to " +
          (parseInt(ragChunkSize) - 1) +
          " characters"
      );
      return;
    } else {
      setRagChunkOverlapError("");
    }

    try {
      setUploading(true);

      console.log("Uploading file", selectedFile);

      //upload the file to S3 and get the location
      const uploadResult = await Storage.put(
        agentId + "/" + selectedFile.name,
        selectedFile,
        {
          level: "public",
          contentType: selectedFile.type,
        }
      );

      console.log("Upload result", uploadResult);
      const location = uploadResult.key;

      const r = await API.post("eventapi", "/uploadDocumentToAgent", {
        body: {
          projectId: projectId,
          agentId: agentId,
          location: location,
          fileName: selectedFile.name,
          fileSize: selectedFile.size.toString(),
          fileType: selectedFile.type,
          uploadDate: new Date().toISOString(),
          uploader: user?.displayName || "",
          lastModifiedDate:
            selectedFile.lastModifiedDate?.toISOString() ||
            new Date(selectedFile.lastModified).toISOString(),
          ownershipAttestation: ownershipAttestation,
          type: "file",
          ragSplitter: ragSplitter,
          ragChunkSize: ragChunkSize,
          ragChunkOverlap: ragChunkOverlap,
        },
      });

      console.log("Upload response", r);

      queryClient.invalidateQueries({
        queryKey: ["LLMAgentDocuments", { agentId: agentId }],
      });

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setOwnershipAttestationError("");
      setOwnershipAttestation("");
    } catch (error) {
      console.error("Error uploading file", error);
    }
    setUploading(false);
  };

  return (
    <Box>
      <Typography variant="body2" mb={1}>
        Upload new or replace existing file...
      </Typography>
      <input
        type="file"
        accept=".pdf,.txt,.cpp,.go,.java,.js,.php,.proto,.python,.py,.rst,.ruby,.rust,.scala,.swift,.markdown,.md,.latex,.html,.sol,.ts,.tsx,.jsx,.c,.csv"
        onChange={handleFileUpload}
        ref={fileInputRef}
      />
      {fileTooBigError && (
        <Typography color="error">{fileTooBigError}</Typography>
      )}

      {selectedFile && (
        <Grid container spacing={2} mt={2}>
          <Grid item xs={3}>
            <TextField
              defaultValue={selectedFile.name}
              name="fileName"
              label="File Name"
              size="small"
              fullWidth
              disabled
              variant="standard"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              defaultValue={selectedFile.size}
              label="File Size (bytes)"
              size="small"
              fullWidth
              disabled
              variant="standard"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              defaultValue={selectedFile.type}
              name="fileType"
              label="File Type"
              size="small"
              fullWidth
              disabled
              variant="standard"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              defaultValue={selectedFile.lastModifiedDate?.toISOString()}
              name="lastModifiedDate"
              label="Last Modified Date"
              size="small"
              fullWidth
              disabled
              variant="standard"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              defaultValue={ownershipAttestation}
              name="ownershipAttestation"
              label="Type your full name to attest this document is owned by you"
              size="small"
              fullWidth
              onChange={(e) => setOwnershipAttestation(e.target.value)}
              error={!!ownershipAttestationError}
              helperText={ownershipAttestationError}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small" error={!!ragSplitterError}>
              <InputLabel id="rag-splitter-label">Document splitter</InputLabel>
              <Select
                labelId="rag-splitter-label"
                value={ragSplitter}
                name="ragSplitter"
                label="Document splitter"
                onChange={(e) => setRagSplitter(e.target.value)}
              >
                <MenuItem value="default">
                  Default (recursive text splitter)
                </MenuItem>
                <MenuItem value="newline">New-line character (/n good for CSV)</MenuItem>
                <MenuItem value="blankline">Blank line (/n/n)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <TextField
            type="number"
              defaultValue={ragChunkSize}
              name="ragChuckSize"
              label={ragSplitter === "default" ? "Chunk Size (characters)" : "Maximum Chunk Size Before Sub-Splitting (characters)"}
              size="small"
              fullWidth
              onChange={(e) => {
                setRagChuckSize(e.target.value)
              }}
              helperText={ragChunkSizeError}
              error={!!ragChunkSizeError}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
            type="number"
              defaultValue={ragChunkOverlap}
              name="ragChuckOverlap"
              label="Chunk Overlap Size (bytes)"
              size="small"
              fullWidth
              onChange={(e) => setRagChuckOverlap(e.target.value)}
              helperText={ragChunkOverlapError}
              error={!!ragChunkOverlapError}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={() => onSubmit()}>
              Upload
            </Button>
            &nbsp;&nbsp;
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedFile(null);
                setOwnershipAttestationError("");
                setOwnershipAttestation("");
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              Cancel
            </Button>
            {uploading && (
              <Typography>
                Uploading... (this can take a few minutes)
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default FileUploader;
